// #region Imports
import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";
import IcAtomBlue from "@/assets/icons/ic-atom-blue.svg";
import { Button } from "@/components/ui/button";
import type { ShortlistAgent } from "@/services/shortlist-service";
import { useLocation, useNavigate } from "react-router-dom";
// #endregion

// #region Helpers
const SOLUTION_PREVIEW_MAX = 150;

const truncateSolution = (text: string | null): string => {
  if (!text) return "N/A";
  const startMarker = "[도입 효과]:";
  const endMarker = "[핵심 차별화 포인트]:";
  const startIdx = text.indexOf(startMarker);
  const endIdx = text.indexOf(endMarker);

  // No markers (catalog-imported text) → show the text itself, capped like a summary
  if (startIdx === -1 || endIdx === -1) {
    const plain = text.trim();
    return plain.length > SOLUTION_PREVIEW_MAX ? `${plain.slice(0, SOLUTION_PREVIEW_MAX).trimEnd()}…` : plain;
  }

  const contentStart = startIdx + startMarker.length;
  // Nothing between markers → N/A
  if (contentStart >= endIdx) return "N/A";

  return text.substring(contentStart, endIdx).trim().replace(/,+$/, "");
};
// #endregion

// #region Main Component — AgentComparePage
const AgentComparePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // #region State / Data
  const stateAgents: ShortlistAgent[] = location.state?.agents ?? [];
  const projectId: string | undefined = location.state?.projectId;
  console.log(stateAgents);

  const agents = stateAgents.map((a) => ({
    id: a.agent_id,
    name: a.name,
    provider: a.provider,
    short_description: a.short_description,
    solution: truncateSolution(a.solution),
    categories: a.categories,
    application_area: a.application_area,
    selected_count: a.selected_count,
    main_category: a.main_category,
    created_at: "",
    updated_at: null,
  }));

  return (
    <div className="flex flex-col items-center gap-12 pb-10 mx-auto max-w-[1120px] px-5 min-[1120px]:px-0 pt-6 lg:pt-10 w-full">
      {/* #region PC Header */}

      <div className="hidden lg:flex items-center gap-4 w-full">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="w-10 h-10 lg:w-12 lg:h-12"
        >
          <img src={icArrowLeft} alt="Icon Arrow Left" />
        </Button>
        <div className="flex flex-col gap-1 flex-1">
          <h1 className="text-xl font-bold leading-7 tracking-[-0.02em] text-[#202020]">
            Agent Shortlist Comparison
          </h1>
          <p className="text-sm leading-5 tracking-[-0.01em] text-[#000000]">
            {agents.length}개 에이전트의 주요 기능 및 특징을 비교해보세요
          </p>
        </div>
      </div>
      {/* #endregion PC Header */}

      {/* #region Mobile Header */}
      <div className="flex flex-col gap-4 w-full lg:hidden">
        <div className="flex flex-col items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0 w-12 h-12"
            aria-label="뒤로가기"
          >
            <img src={icArrowLeft} alt="" />
          </Button>
          <div className="flex flex-col gap-1 flex-1">
            <h1 className="text-xl font-bold leading-6 tracking-[-0.018em] text-[#202020]">
              Agent Shortlist Comparison
            </h1>
            <p className="text-sm leading-5 tracking-[-0.01em] text-[#000000]">
              {agents.length}개 에이전트의 주요 기능 및 특징을 비교해보세요
            </p>
          </div>
        </div>
        <div className="w-full h-px bg-black/[0.09]" />
      </div>
      {/* #endregion Mobile Header */}
      {agents.length === 0 ? (
        // #region Empty State
        <div className="flex flex-col items-center justify-center py-20 w-full">
          <p className="text-base text-[#8D8D8D]">매칭된 에이전트가 없습니다.</p>
        </div>
      ) : (
        // #endregion
        <>
          {/* #region PC Table Layout */}
          <div className="hidden lg:flex flex-col w-full border border-black/[0.09] rounded-none overflow-hidden">
            {/* Row 1: Agent Header */}
            <div className="flex w-full">
              <div className="w-[188px] shrink-0 border-r border-b border-black/[0.09] p-3" />
              {agents.map((agent, index) => {
                // const color = AGENT_ICON_COLORS[index % AGENT_ICON_COLORS.length];
                return (
                  <div
                    key={agent.id}
                    className="flex-1 flex items-start justify-center gap-2 px-3 py-6 border-b border-r last:border-r-0 border-black/[0.09]"
                  >
                    <div className="flex flex-col items-center gap-6">
                      <div className="w-12 h-12 rounded-none flex items-center justify-center p-1">
                        <img src={IcAtomBlue} alt="" />
                      </div>
                      <span className="text-base font-bold leading-6 tracking-[-0.01em] text-[#000000] text-center">
                        {agent.name}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Row 2: 간단한 소개 */}
            <CompareRow label="간단한 소개" agents={agents} field="short_description" />

            {/* Row 3: 솔루션 소개 */}
            <CompareRow label="솔루션 소개" agents={agents} field="solution" centerText />

            {/* Row 4: 대분류 */}
            <div className="flex w-full">
              <TableLabel>대분류</TableLabel>
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className="flex-1 flex justify-center items-start p-3 border-r border-b last:border-r-0 border-black/[0.09]"
                >
                  <p className="text-base leading-6 tracking-[-0.01em] text-[#000000] text-center w-full">
                    {agent.main_category ? agent.main_category : ""}
                  </p>
                </div>
              ))}
            </div>

            {/* Row 5: 기능 카테고리 */}
            <div className="flex w-full">
              <TableLabel>기능 카테고리</TableLabel>
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className="flex-1 flex justify-center items-start p-3 border-r border-b last:border-r-0 border-black/[0.09]"
                >
                  <div className="flex flex-wrap gap-2 w-full">
                    <span
                      className={`inline-flex items-start justify-center  text-[16px] text-[#000000] w-fit whitespace-pre-line`}
                    >
                      {agent.categories}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Row 6: 적용 분야 */}
            <CompareRow label="적용 분야" agents={agents} field="application_area" />
          </div>
          {/* #endregion PC Table Layout */}

          {/* #region Mobile Card Layout */}
          <div className="flex flex-col gap-4 w-full lg:hidden">
            {agents.map((agent) => (
              <div key={agent.id} className="flex items-center gap-4 w-full">
                <div className="flex-1 bg-[#F9F9F9] border border-black/[0.09] rounded">
                  <div className="px-2 py-3">
                    <span className="text-base font-bold leading-6 tracking-[-0.01em] text-[#000000]">
                      {agent.name}
                    </span>
                  </div>
                  <div className="flex flex-col gap-4 bg-white rounded p-4 shadow-[0px_4px_10px_-8px_rgba(0,0,0,0.09)]">
                    <MobileField label="간단한 소개" value={agent.short_description} />
                    <MobileField label="솔루션 소개" value={agent.solution} />
                    <MobileField
                      label="대분류"
                      value={agent.main_category ? agent.main_category : ""}
                    />
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-bold leading-5 tracking-[-0.01em] text-[#202020]">
                        기능 카테고리
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <span
                          className={`text-[16px] leading-4 text-[#000000] w-fit  whitespace-pre-line`}
                        >
                          {agent.categories}
                        </span>
                      </div>
                    </div>
                    <MobileField label="적용 분야" value={agent.application_area} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* #endregion Mobile Card Layout */}
        </>
      )}
    </div>
  );
};
// #endregion

export default AgentComparePage;

// #region Sub-components

/**
 * Reusable table label cell (fixed 188px width) for the PC comparison table.
 */
const TableLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="w-[188px] shrink-0 flex items-center gap-2 p-3 border-r border-b border-black/[0.09]">
    <span className="text-sm font-bold leading-5 tracking-[-0.01em] text-[#000000]">
      {children}
    </span>
  </div>
);

/**
 * Generic comparison row for text fields in the PC table.
 * Renders a label cell + one data cell per agent.
 */
const CompareRow = ({
  label,
  agents,
  field,
  centerText = false,
}: {
  label: string;
  agents: any[];
  field: string;
  centerText?: boolean;
}) => (
  <div className="flex w-full">
    <TableLabel>{label}</TableLabel>
    {agents.map((agent) => (
      <div
        key={agent.id}
        className="flex-1 flex items-start p-3 border-r border-b last:border-r-0 border-black/[0.09]"
      >
        <p
          className={`text-base leading-6 tracking-[-0.01em] text-[#000000] ${centerText ? "text-center w-full" : ""}`}
        >
          {String(agent[field] ?? "")}
        </p>
      </div>
    ))}
  </div>
);

/**
 * Label-value pair used inside mobile agent cards.
 */
const MobileField = ({ label, value }: { label: string; value: string | null }) => (
  <div className="flex flex-col gap-1">
    <span className="text-sm font-bold leading-5 tracking-[-0.01em] text-[#000000]">{label}</span>
    <span className="text-base leading-6 tracking-[-0.01em] text-[#000000]">{value ?? ""}</span>
  </div>
);

// #endregion
